import React, { useState, useMemo } from "react";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SearchOutlined,
  GoogleOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useUsers, useDeleteUser, useUpdateUser } from "~/services/user";
import { TUser } from "~/types";
import { Role } from "~/utils/constants";
import { CreateUserForm } from "../admin-management/create-user-form";
import { EditUserForm } from "../admin-management/edit-user-form";
import { useAuth } from "~/store";

const { Title } = Typography;
const { Option } = Select;

export const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<Role | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TUser | null>(null);
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const queryParams = useMemo(() => ({
    page: currentPage,
    pageSize,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(roleFilter ? { roles: [roleFilter] } : {}),
  }), [currentPage, pageSize, debouncedSearch, roleFilter]);

  const {
    data: usersData,
    isLoading,
    refetch,
  } = useUsers(queryParams);

  const deleteUserMutation = useDeleteUser();
  const updateUserMutation = useUpdateUser();

  // Only show if current user is SUPER_ADMIN or SYSTEM_ADMIN
  if (currentUser?.role !== Role.SUPER_ADMIN && currentUser?.role !== Role.SYSTEM_ADMIN) {
    return (
      <Card>
        <div className="text-center py-8">
          <UserOutlined className="text-4xl text-gray-400 mb-4" />
          <Title level={4} className="text-gray-500">
            You don't have permission to access this page
          </Title>
        </div>
      </Card>
    );
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return "red";
      case Role.ADMIN:
        return "blue";
      case Role.LEAD:
        return "orange";
      case Role.STAFF:
        return "green";
      case Role.CLIENT:
        return "default";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return "Super Admin";
      case Role.ADMIN:
        return "Admin";
      case Role.LEAD:
        return "Lead";
      case Role.STAFF:
        return "Staff";
      case Role.CLIENT:
        return "Client";
      default:
        return role;
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(
      setTimeout(() => {
        setDebouncedSearch(value);
        setCurrentPage(1);
      }, 400)
    );
  };

  const handleRoleFilter = (value: Role | undefined) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setRoleFilter(undefined);
    setSearch("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns: ColumnsType<TUser> = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (username) => (
        <Space>
          <UserOutlined />
          <span>{username}</span>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phone) => phone || "-",
    },
    {
      title: "Google",
      key: "google",
      width: 80,
      align: "center" as const,
      render: (_, record) =>
        record.googleId ? (
          <Tag icon={<GoogleOutlined />} color="blue">
            {record.googleEmail || "Linked"}
          </Tag>
        ) : (
          <span className="text-gray-300">-</span>
        ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("en-US"),
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) =>
        record.deactivatedAt ? (
          <Tag color="red">Inactive</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isDeactivated = !!record.deactivatedAt;
        const isSelf = record.id === currentUser?.id;
        return (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUser(record);
                setIsEditModalVisible(true);
              }}
            />
            <Popconfirm
              title={isDeactivated ? "Reactivate User" : "Deactivate User"}
              description={
                isDeactivated
                  ? "Are you sure you want to reactivate this user?"
                  : "This will prevent the user from logging in. Are you sure?"
              }
              onConfirm={() => {
                updateUserMutation.mutate({
                  id: record.id,
                  userData: { deactivate: !isDeactivated },
                });
              }}
              okText={isDeactivated ? "Reactivate" : "Deactivate"}
              cancelText="Cancel"
            >
              <Button
                type="text"
                icon={isDeactivated ? <CheckCircleOutlined /> : <StopOutlined />}
                disabled={isSelf}
                style={{ color: isDeactivated ? "#52c41a" : undefined }}
              />
            </Popconfirm>
            <Popconfirm
              title="Delete User"
              description="This will permanently delete the user. This action cannot be undone. Are you sure?"
              onConfirm={() => {
                deleteUserMutation.mutate(record.id);
              }}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isSelf}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} className="mb-0">
              User Management
            </Title>
            <p className="text-gray-500 mt-2">
              Manage user accounts in the system
            </p>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalVisible(true)}
            >
              Create New User
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Filters */}
      <Card>
        <Row gutter={16}>
          <Col span={12}>
            <Input
              placeholder="Search by name, email..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={handleSearchChange}
              allowClear
              onClear={() => {
                setSearch("");
                setDebouncedSearch("");
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by role"
              style={{ width: "100%" }}
              value={roleFilter}
              onChange={handleRoleFilter}
              onClear={() => handleRoleFilter(undefined)}
              allowClear
            >
              <Option value={Role.SUPER_ADMIN}>Super Admin</Option>
              <Option value={Role.ADMIN}>Admin</Option>
              <Option value={Role.STAFF}>Staff</Option>
              <Option value={Role.CLIENT}>Client</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button onClick={handleReset}>Reset</Button>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={usersData?.items}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: usersData?.meta?.totalItems,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CreateUserForm
          onSuccess={() => {
            setIsCreateModalVisible(false);
            refetch();
          }}
          onCancel={() => setIsCreateModalVisible(false)}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {selectedUser && (
          <EditUserForm
            user={selectedUser}
            onSuccess={() => {
              setIsEditModalVisible(false);
              setSelectedUser(null);
              refetch();
            }}
            onCancel={() => {
              setIsEditModalVisible(false);
              setSelectedUser(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};
